import React from "react";

/**
 * The one rounded "pill" used across the app for tags, prompt answers, and
 * option pickers — so the profile editor, the public profile, and the quiz
 * selectors all read as the same family instead of four different chip looks.
 *
 * Variants map to roles rather than colours:
 *   • solid   — a committed/selected choice (e.g. the picked option)
 *   • soft    — a value shown on a profile (your tags, what others see)
 *   • outline — an offered-but-not-picked choice (a suggestion to add)
 *   • dashed  — an empty "Add" affordance
 *
 * Renders as a <button> when `onClick` is given, otherwise a <span>. Pass
 * `leading`/`trailing` for emoji or icons (the ✕ on a removable tag, the + on a
 * suggestion).
 */
type PillVariant = "solid" | "soft" | "outline" | "dashed";
type PillSize = "sm" | "md";

const VARIANTS: Record<PillVariant, string> = {
  solid:
    "bg-primary-500/20 text-white border border-primary-700",
  soft:
    "bg-primary-500/20 text-primary-700 dark:text-white border border-primary-700",
  outline:
    "bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-700",
  dashed:
    "bg-primary-500/[0.04] text-primary-600 dark:text-primary-400 border border-dashed border-primary-400/50 hover:bg-primary-500/10",
};

const SIZES: Record<PillSize, string> = {
  sm: "px-3 py-1 text-xs gap-1",
  md: "px-3.5 py-2 text-sm gap-1.5",
};

interface PillProps {
  children: React.ReactNode;
  variant?: PillVariant;
  size?: PillSize;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  "aria-pressed"?: boolean;
  "aria-label"?: string;
}

export function Pill({
  children,
  variant = "soft",
  size = "md",
  leading,
  trailing,
  onClick,
  disabled = false,
  className = "",
  ...aria
}: PillProps) {
  const clickable = !!onClick && !disabled;
  const classes = [
    "inline-flex items-center max-w-full rounded-full font-medium transition-all duration-200",
    SIZES[size],
    VARIANTS[variant],
    clickable ? "cursor-pointer hover:shadow-sm active:scale-95" : "",
    disabled ? "opacity-40 cursor-not-allowed" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {leading && <span className="leading-none shrink-0">{leading}</span>}
      <span className="truncate">{children}</span>
      {trailing && <span className="leading-none shrink-0">{trailing}</span>}
    </>
  );

  if (clickable) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={classes} {...aria}>
        {content}
      </button>
    );
  }
  return (
    <span className={classes} {...aria}>
      {content}
    </span>
  );
}

export default Pill;
